import { BRAND } from "@/data/content";

/**
 * LogoMark
 *
 * Renders the raster BLEZZINGS logo (black glyph on white background)
 * as a resolution-independent SVG in which the white background is made
 * fully transparent via an feColorMatrix filter.
 *
 * Result:
 *   - Only the actual black wing + wordmark pixels remain opaque.
 *   - The image sits cleanly on ANY surface colour (light OR dark) without
 *     any white "sticker" box around it.
 *
 * `variant`:
 *   - "dark"  → renders the mark in near-black (default, use on light bg)
 *   - "light" → renders the mark in white (use on dark bg)
 */
export default function LogoMark({
  className = "",
  variant = "dark",
  ariaLabel = "BLEZZINGS",
  testid,
}) {
  // Alpha derived from source luminance (approx): white → 0, black → 1,
  // grey → partial. Coefficients ≈ Rec.709 luma so edges anti-alias cleanly.
  const alphaRow = "-0.2126 -0.7152 -0.0722 0 1";
  const inkRow =
    variant === "light"
      ? "0 0 0 0 1 0 0 0 0 1 0 0 0 0 1" // pure white ink
      : "0 0 0 0 0.02 0 0 0 0 0.02 0 0 0 0 0.02"; // near-black ink

  const values = `${inkRow.split(" ").slice(0, 5).join(" ")} ` + // R row
                 `${inkRow.split(" ").slice(5, 10).join(" ")} ` + // G row
                 `${inkRow.split(" ").slice(10, 15).join(" ")} ` + // B row
                 `${alphaRow}`; // A row

  // Stable per-variant filter id
  const filterId = `blz-logo-${variant}`;

  return (
    <svg
      data-testid={testid}
      role="img"
      aria-label={ariaLabel}
      viewBox="99 231 1751 303"
      preserveAspectRatio="xMidYMid meet"
      className={className}
    >
      <defs>
        <filter id={filterId} colorInterpolationFilters="sRGB">
          <feColorMatrix type="matrix" values={values} />
        </filter>
      </defs>
      <image
        href={BRAND.logoUrl}
        x="0"
        y="0"
        width="1947"
        height="808"
        filter={`url(#${filterId})`}
        preserveAspectRatio="xMidYMid meet"
      />
    </svg>
  );
}
